/**
 * Ejector — 1D entrainment model (motive jet entrains a suction stream).
 *
 * A high-pressure MOTIVE stream expands through a converging(-diverging) nozzle,
 * creating a high-velocity, low-pressure jet that ENTRAINS a low-pressure SUCTION
 * stream. The two streams mix (here: constant-pressure mixing) and the combined
 * stream is decelerated in a DIFFUSER, recovering static pressure to the discharge.
 *
 * Stages (classic 1D ejector theory):
 *   1. Motive nozzle  : expand from P_motive to the mixing pressure (~P_suction),
 *                       u_m = sqrt(2·η_nozzle·(P_motive − P_mix)/ρ_motive)   [Bernoulli]
 *   2. Mixing section : constant-pressure momentum mixing,
 *                       u_mix = η_mix·(u_m + ω·u_s)/(1 + ω),  ω = ṁ_suction/ṁ_motive
 *   3. Diffuser       : pressure recovery from kinetic energy,
 *                       P_out = P_mix + η_diffuser·ρ_mix·u_mix²/2
 *
 * Conservation (enforced):
 *   Mass:     ṁ_motive + ṁ_suction = ṁ_out                         (ω = ṁ_s/ṁ_m)
 *   Momentum: ṁ_motive·u_m + ṁ_suction·u_s = ṁ_out·u_mix / η_mix
 *   Energy:   ṁ_motive·h_motive + ṁ_suction·h_suction = ṁ_out·h_out (mass-weighted)
 *
 * ENTRAINMENT RATIO (ω):
 *   ω is, physically, an OUTPUT of the device — it falls as the required pressure
 *   lift (discharge pressure) rises. Two modes are supported:
 *     • Explicit: pass `entrainment_ratio` and it is used verbatim (design point).
 *     • Computed: if `entrainment_ratio` is omitted, ω is solved from the
 *       constant-pressure-mixing + diffuser balance so that the diffuser exactly
 *       produces the requested discharge pressure `P_discharge`. Larger lift → smaller ω,
 *       matching measured ejector characteristics.
 *
 * MODEL / CITATIONS  (see flagsForUser — these are engineering assumptions):
 *   - 1D constant-pressure-mixing ejector theory: Keenan, Neumann & Lustwerk (1950);
 *     Huang, Chang, Wang & Petrenko, "A 1-D analysis of ejector performance",
 *     Int. J. Refrigeration 22 (1999) 354–364; ESDU 86030 "Ejectors and jet pumps".
 *   - Incompressible-Bernoulli nozzle velocity is a first-order estimate (motive is
 *     compressible; ρ_motive is the inlet density, so u_m is conservative/under-predicted).
 *   - Suction inlet velocity u_s ≈ 0.1·u_m (entrained stream is near-stagnant).
 *   - Default efficiencies (η_nozzle=0.85, η_mix=0.95, η_diffuser=0.85) are typical
 *     literature values for steam/refrigerant ejectors; they are NOT fitted to a rig.
 *
 * In the NS network the ejector sees the motive stream on the inlet junction and
 * discharges on the outlet junction; the suction stream is provided via params.
 *
 * MASS BOOKKEEPING — BOUNDARY-ESTIMATOR ONLY (N5, documented limitation):
 * the entrained SUCTION mass never enters the pipe network. The network
 * carries only the motive throughflow; deltaH is the mixed-minus-motive
 * enthalpy applied at the motive mass flow, so the OUTLET STREAM ENERGY is
 * per-unit-MOTIVE-mass, not per-unit-mixed-mass. Constraining the outlet face
 * to ṁ_motive·(1+ω) was evaluated and REJECTED: nsNetwork enforces `mdot`
 * only on outlet faces while junction continuity (Step 5) still sums the
 * un-augmented inflow, so the injected ω·ṁ_motive would register as a
 * permanent junction mass imbalance and the momentum residual could never
 * converge (CLAUDE.md rule 1 — never break convergence). Until the suction
 * stream is modelled as a real network branch, treat the ejector's outlet
 * P/h as a boundary ESTIMATE (metadata flag `suction_mass_in_network: 0`).
 */
import type { NSComponent, Junction, ComponentUpdate } from '../types';
import { propsFromPH_SI } from '../core/fluidAdapter';

/** Suction inlet velocity as a fraction of the motive jet velocity (near-stagnant). */
const SUCTION_VELOCITY_FRACTION = 0.1;
/** Upper bound on a physically meaningful entrainment ratio (jet pumps rarely exceed ~5). */
const OMEGA_MAX = 5.0;
/** Lower bound — strictly positive; a non-positive ω means the lift cannot be met. */
const OMEGA_MIN = 1e-4;

export class NSEjector implements NSComponent {
  id: string;
  type = 'ejector';
  params: Record<string, any>;

  constructor(id: string, params: {
    // ── Efficiencies ──
    nozzle_efficiency?: number;   // η_nozzle  (default 0.85)
    diffuser_efficiency?: number; // η_diffuser(default 0.85)
    mixing_efficiency?: number;   // η_mix     (default 0.95)

    // ── Entrainment ──
    entrainment_ratio?: number;   // ω = ṁ_suction/ṁ_motive. If given, used verbatim.
                                  // If omitted, ω is COMPUTED from the pressure lift.

    // ── Motive stream (defaults to the inlet junction at evaluation time) ──
    P_motive?: number;            // Pa
    h_motive?: number;            // J/kg
    mdot_motive?: number;         // kg/s

    // ── Suction stream ──
    P_suction?: number;           // Pa — suction pressure (default: 50% of motive)
    h_suction?: number;           // J/kg — suction enthalpy (default: same as motive)
    mdot_suction?: number;        // kg/s — used to derive ω if entrainment_ratio omitted

    // ── Discharge demand (back-pressure the ejector must lift to) ──
    P_discharge?: number;         // Pa — target outlet pressure (default: outlet junction)
  }) {
    this.id = id;
    // Record whether ω was *explicitly* supplied BEFORE merging defaults — an explicit
    // ω pins the design point, whereas the default 0.3 only applies when no discharge
    // demand (P_discharge / outlet pressure) or suction mass flow is available to
    // COMPUTE ω from the pressure lift.
    const omegaExplicit = params.entrainment_ratio !== undefined;
    this.params = {
      nozzle_efficiency: 0.85,
      diffuser_efficiency: 0.85,
      mixing_efficiency: 0.95,
      entrainment_ratio: 0.3, // legacy default; overridden by computed-from-lift unless explicit
      ...params,
      _omegaExplicit: omegaExplicit,
    };
  }

  applyBoundaryConditions(inlet: Junction, outlet: Junction, _dt: number): ComponentUpdate {
    const { nozzle_efficiency, diffuser_efficiency, mixing_efficiency } = this.params;

    // Motive stream: prefer explicit params, else the inlet junction.
    const P_motive = this.params.P_motive ?? inlet.P;
    const h_motive = this.params.h_motive ?? inlet.h;

    // Suction stream defaults.
    const P_suction = this.params.P_suction ?? P_motive * 0.5;
    const h_suction = this.params.h_suction ?? h_motive;

    // Discharge demand: explicit, else the outlet junction pressure, else a mild recovery.
    const P_discharge =
      this.params.P_discharge ??
      (Number.isFinite(outlet?.P) && outlet.P > P_suction ? outlet.P : undefined);

    try {
      // Guard inputs (NaN / non-physical pressures handled by the catch fallback below).
      if (!(P_motive > 0) || !(P_suction > 0) || !(P_motive > P_suction)) {
        throw new Error('non-physical ejector pressures');
      }

      const props_motive = propsFromPH_SI(P_motive, h_motive);
      const rho_motive = props_motive.rho;
      const props_suction = propsFromPH_SI(P_suction, h_suction);
      const rho_suction = props_suction.rho;

      // 1. Motive nozzle — expand to the mixing pressure (≈ P_suction).
      //    Incompressible Bernoulli with nozzle efficiency (first-order; see header).
      const dP_nozzle = Math.max(0, P_motive - P_suction);
      const u_nozzle = Math.sqrt(2 * nozzle_efficiency * dP_nozzle / rho_motive);
      const u_suction = u_nozzle * SUCTION_VELOCITY_FRACTION;

      // 2. Entrainment ratio ω — explicit pins the design point; otherwise derive from
      //    the suction mass flow, or COMPUTE it from the discharge pressure lift.
      let omega: number;
      let omegaMode: number; // 0 = explicit, 1 = from mdot, 2 = computed-from-lift, 3 = legacy default
      if (this.params._omegaExplicit) {
        omega = this.params.entrainment_ratio;
        omegaMode = 0;
      } else if (
        this.params.mdot_suction !== undefined &&
        this.params.mdot_motive !== undefined &&
        this.params.mdot_motive > 0
      ) {
        omega = this.params.mdot_suction / this.params.mdot_motive;
        omegaMode = 1;
      } else if (P_discharge !== undefined) {
        omega = this.solveEntrainmentForDischarge(
          u_nozzle, u_suction, rho_motive, rho_suction,
          P_suction, P_discharge, mixing_efficiency, diffuser_efficiency,
        );
        omegaMode = 2;
      } else {
        // No explicit ω, no suction flow, no lift target → legacy default.
        omega = this.params.entrainment_ratio ?? 0.3;
        omegaMode = 3;
      }
      // Physical clamp: ω strictly positive, bounded.
      omega = Math.min(OMEGA_MAX, Math.max(OMEGA_MIN, omega));

      // 3. Constant-pressure momentum mixing.
      const u_mix = mixing_efficiency * (u_nozzle + omega * u_suction) / (1 + omega);

      // 4. Diffuser — recover static pressure from the mixed kinetic energy.
      // N5 FIX: homogeneous mixture density is VOLUME-weighted (specific
      // volumes add mass-weighted):
      //     1/ρ_mix = (1/ρ_m + ω/ρ_s) / (1 + ω)
      // The previous MASS-weighted form (ρ_m + ω·ρ_s)/(1+ω) overestimated
      // ρ_mix — e.g. ρ_m = 5, ρ_s = 0.5, ω = 1 gave 2.75 instead of the
      // correct 0.909 kg/m³ — inflating P_recovery ≈ 3× for disparate-density
      // streams. Same reciprocal rule as frictionCorrelations.mixtureDensity.
      const rho_mix = (1 + omega) / (1 / rho_motive + omega / rho_suction);
      const P_recovery = diffuser_efficiency * rho_mix * u_mix * u_mix / 2;
      let P_out = P_suction + P_recovery;
      // Physical bound: discharge must lie strictly between suction and motive.
      P_out = Math.min(P_motive, Math.max(P_suction, P_out));

      // Energy: mass-weighted (adiabatic) enthalpy mixing. h_out is referenced to the
      // MIXED stream — the network applies deltaH to carry the inlet (motive) onto the
      // combined outlet enthalpy.
      const h_out = (h_motive + omega * h_suction) / (1 + omega);
      const deltaH = h_out - h_motive;
      const deltaP = P_out - P_motive;

      // ── Conservation residuals (for validation / tests) ──
      // Energy: per-unit-motive-mass balance.  ṁ_m·h_m + ṁ_s·h_s = ṁ_out·h_out
      // Dividing by ṁ_m:  h_m + ω·h_s = (1+ω)·h_out  → residual should be ~0.
      const energy_residual = Math.abs((h_motive + omega * h_suction) - (1 + omega) * h_out);
      const energy_scale = Math.abs(h_motive) + Math.abs(omega * h_suction) + 1;
      const energy_residual_frac = energy_residual / energy_scale;

      return {
        deltaP,
        deltaH,
        momentumSource: 0,
        energySource: 0, // Adiabatic mixing — no external heat/work.
        metadata: {
          u_nozzle,
          u_suction,
          u_mix,
          rho_mix,
          P_recovery,
          P_out,
          P_suction,
          P_discharge: P_discharge ?? P_out,
          h_out,
          entrainment_ratio: omega,
          _entrainmentRatio: omega, // consumed by stateSync to surface back to the node
          omega_mode: omegaMode,
          eta_nozzle: nozzle_efficiency,
          eta_mix: mixing_efficiency,
          eta_diffuser: diffuser_efficiency,
          energy_residual_frac,
          // N5: the entrained mass is NOT injected into the pipe network —
          // see the module-header "MASS BOOKKEEPING" note. 0 = estimator-only.
          suction_mass_in_network: 0,
        },
      };
    } catch {
      // Fallback: passthrough (non-physical inputs or property lookup failure).
      return {
        deltaP: 0,
        deltaH: 0,
        momentumSource: 0,
        energySource: 0,
        metadata: {
          eta_nozzle: nozzle_efficiency,
          eta_diffuser: diffuser_efficiency,
          error: 1,
        },
      };
    }
  }

  /**
   * Solve the entrainment ratio ω required to lift the mixed stream from P_suction to
   * P_discharge through the diffuser, using constant-pressure-mixing + diffuser theory.
   *
   * Diffuser:  P_discharge − P_suction = η_diff·ρ_mix(ω)·u_mix²/2   ⇒  u_mix_req(ω)
   * Mixing:    u_mix(ω) = η_mix·(u_m + ω·u_s)/(1 + ω)
   * Root of    g(ω) = u_mix(ω) − u_mix_req(ω) = 0  on ω ∈ [OMEGA_MIN, OMEGA_MAX].
   *
   * NUMERICAL METHOD (changed with the N5 density fix): BISECTION.
   * g(ω) is STRICTLY DECREASING — u_mix falls as more slow fluid is entrained
   * (u_s < u_m), while u_mix_req rises because the volume-weighted ρ_mix drops
   * as the light suction stream dominates the mixture volume. A monotone
   * residual has a unique bracketed root, so bisection converges
   * unconditionally. The previous relaxed fixed-point iteration was only
   * stable against the OLD mass-weighted density (which varied weakly with ω);
   * with the corrected volume-weighted ρ_mix it stepped through NEGATIVE ω —
   * producing negative mixture densities — and diverged to the clamp floor.
   *
   * Saturation (physical): g > 0 across the whole range ⇒ the lift is easy —
   * entrainment maxes out at OMEGA_MAX; g < 0 everywhere ⇒ the lift is not
   * achievable even at minimal entrainment — returns OMEGA_MIN.
   * Larger lift ⇒ smaller ω throughout (measured ejector characteristic).
   */
  private solveEntrainmentForDischarge(
    u_m: number, u_s: number,
    rho_motive: number, rho_suction: number,
    P_suction: number, P_discharge: number,
    eta_mix: number, eta_diff: number,
  ): number {
    const lift = Math.max(0, P_discharge - P_suction);

    // g(ω) = achievable mixed velocity − required mixed velocity, both ≥ 0.
    const g = (omega: number): number => {
      // N5: volume-weighted homogeneous mixture density (must match the
      // apply-path diffuser calculation or the solved ω would be inconsistent
      // with the recovery it produces).
      const rho_mix = (1 + omega) / (1 / rho_motive + omega / rho_suction);
      const u_mix_req = Math.sqrt(Math.max(0, 2 * lift / (eta_diff * rho_mix)));
      const u_mix = eta_mix * (u_m + omega * u_s) / (1 + omega);
      return u_mix - u_mix_req;
    };

    let lo = OMEGA_MIN;
    let hi = OMEGA_MAX;
    const gLo = g(lo);
    const gHi = g(hi);
    if (!Number.isFinite(gLo) || gLo <= 0) return OMEGA_MIN; // lift infeasible
    if (Number.isFinite(gHi) && gHi >= 0) return OMEGA_MAX;  // lift trivially met

    // Bisect the sign change (g(lo) > 0 > g(hi)); 60 halvings ⇒ interval
    // below 5·2⁻⁶⁰ — far tighter than any physical uncertainty here.
    for (let i = 0; i < 60; i++) {
      const mid = 0.5 * (lo + hi);
      const gMid = g(mid);
      if (!Number.isFinite(gMid)) break;
      if (gMid > 0) lo = mid; else hi = mid;
    }
    return 0.5 * (lo + hi);
  }
}
